import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getEmailLogById,
  type EmailLogDetailResponse,
} from "../../../Services/EmailLog";
import {
  formatTimestampDateTimePrecise,
} from "../../../utils/dates";
import Modal from "../../../components/Atomos/Modals/Modal";
import Button from "../../../components/staff/Button";
import { toast } from "react-toastify";
import EmailStatsStatusBadge from "./EmailStatsStatusBadge";
import EmailStatsTemplatePreviewModal from "./EmailStatsTemplatePreviewModal";

function MetaField ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-w-0 flex flex-col gap-1">
      <p className="text-base font-semibold text-gray-font">{label}</p>
      <div className="text-base text-gray-font break-words">
        {value != null && value !== "" ? value : "—"}
      </div>
    </div>
  );
}

function timelineDotClass (status: string, isLatest: boolean): string {
  if (isLatest) {
    if (status === "Delivered" || status === "Opened" || status === "Clicked") {
      return "bg-green-500";
    }
    if (status === "Failed" || status === "Bounced") {
      return "bg-red-500";
    }
    if (status === "Processing") {
      return "bg-blue-500";
    }
  }
  return "bg-gray-300";
}

/** Dark-theme JSON tokens matching ACS-style event viewer. */
const JSON_TOKEN = {
  key: "#7EB8DA",
  string: "#E8B98A",
  number: "#E8D4A0",
  keyword: "#E8D4A0",
  brace: "#E8A0C0",
  punct: "#E6EDF3",
} as const;

function highlightJson (json: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re =
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}\[\]]|[:,]|[^\s"{}\[\]:,]+|\s+/g;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(json)) !== null) {
    const [full, str, colonAfterKey, keyword] = match;
    if (str != null && colonAfterKey != null) {
      nodes.push(
        <span key={key++} style={{ color: JSON_TOKEN.key }}>
          {str}
        </span>,
      );
      nodes.push(
        <span key={key++} style={{ color: JSON_TOKEN.punct }}>
          {colonAfterKey}
        </span>,
      );
      continue;
    }
    if (/^\s+$/.test(full)) {
      nodes.push(full);
      continue;
    }
    let color: string = JSON_TOKEN.punct;
    if (str != null) {
      color = JSON_TOKEN.string;
    } else if (keyword != null) {
      color = JSON_TOKEN.keyword;
    } else if (/^-?\d/.test(full)) {
      color = JSON_TOKEN.number;
    } else if (/^[{}\[\]]$/.test(full)) {
      color = JSON_TOKEN.brace;
    }
    nodes.push(
      <span key={key++} style={{ color }}>
        {full}
      </span>,
    );
  }
  return nodes;
}

function JsonViewer ({ json }: { json: string }): React.ReactElement {
  return (
    <pre
      className="max-h-[min(60vh,32rem)] overflow-auto rounded-b-md bg-[#161B22] p-5 font-mono text-[13px] leading-relaxed text-[#E6EDF3] whitespace-pre-wrap break-all"
      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
    >
      {highlightJson(json)}
    </pre>
  );
}

export default function EmailStatsDetailPage (): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const [log, setLog] = useState<EmailLogDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      setLog(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getEmailLogById(id)
      .then((doc) => {
        if (!cancelled) {
          setLog(doc);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLog(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const currentStatus = useMemo(() => {
    if (!log) {
      return "";
    }
    const fromTimeline = log.timeline?.[log.timeline.length - 1]?.status;
    return String(log.displayStatus || fromTimeline || "").trim();
  }, [log]);

  const rawJson = useMemo(() => {
    if (!log) {
      return "";
    }
    try {
      return JSON.stringify(log, null, 2);
    } catch {
      return String(log);
    }
  }, [log]);

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(rawJson);
      toast.success("JSON copied to clipboard");
    } catch {
      toast.error("Could not copy JSON");
    }
  };

  return (
    <main className="text-left">

      {loading ? null : !log ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <p className="text-gray-600">Email log not found.</p>
          <div className="">
            <Link
              to="/email-stats"
              className="text-accent-Default hover:underline font-medium"
            >
              ← Back to Email Stats
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="bg-accent-Default-dark rounded-lg overflow-hidden shadow-sm">
            <div className="flex justify-end py-3 px-6">
              <Button
                text="View JSON"
                color="white"
                variant="outlined"
                size="fixed"
                onClick={() => setJsonModalOpen(true)}
                className="!w-auto"
              />
            </div>
            <div className="bg-white border rounded-lg border-gray-200 p-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 md:grid-flow-col md:grid-rows-7 lg:grid-rows-5 md:gap-x-8 md:gap-y-4">
              <MetaField
                label="Message ID"
                value={log.providerMessageId || "—"}
              />
              <MetaField label="From" value={log.from || "—"} />
              <MetaField label="To" value={log.recipientEmail || "—"} />
              <MetaField label="Subject" value={log.subject || "—"} />
              <MetaField label="Contact" value={log.contact || "—"} />
              <MetaField label="Firm" value={log.firm || "—"} />
              <MetaField
                label="Template ID"
                value={
                  log.emailNumber != null ? (
                    <div
                      className="text-Default font-medium hover:underline cursor-pointer"
                      onClick={() => setTemplatePreviewOpen(true)}
                    >
                      {log.emailNumber}
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <MetaField label="Category" value={log.category || "—"} />
              <MetaField label="Event" value={log.eventTitle || "—"} />
              <MetaField
                label="Date Scheduled"
                value={formatTimestampDateTimePrecise(
                  log.dateScheduled ?? log.scheduledAt ?? undefined,
                )}
              />
              <MetaField
                label="Date Sent"
                value={formatTimestampDateTimePrecise(log.dateSent ?? undefined)}
              />
              <MetaField
                label="Date Delivered"
                value={formatTimestampDateTimePrecise(
                  log.dateDelivered ?? log.deliveredAt ?? undefined,
                )}
              />
              <MetaField
                label="Processed Date"
                value={formatTimestampDateTimePrecise(
                  log.dateSent ?? log.processedAt ?? log.sentAt,
                )}
              />
              <MetaField
                label="Status"
                value={<EmailStatsStatusBadge status={currentStatus} />}
              />
            </div>
          </div>
          <div className=" bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-font mb-4">
              Delivery Timeline
            </h2>
            {!log.timeline?.length ? (
              <p className="text-sm text-gray-500">No status events recorded.</p>
            ) : (
              <ol className="relative ml-3 border-l border-gray-200">
                {log.timeline.map((entry, index) => {
                  const isLatest = index === log.timeline.length - 1;
                  return (
                    <li
                      key={`${entry.status}-${entry.at}-${index}`}
                      className="mb-4 ml-6 last:mb-0"
                    >
                      <span
                        className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white ${timelineDotClass(
                          entry.status,
                          isLatest,
                        )}`}
                      />
                      <div className="flex items-center gap-x-6">
                        <span className="text-sm tabular-nums text-gray-500 whitespace-nowrap">
                          {formatTimestampDateTimePrecise(entry.at)}
                        </span>
                        <EmailStatsStatusBadge status={entry.status} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className="mt-6">
            <Link
              to="/email-stats"
              className="text-accent-Default hover:underline font-medium"
            >
              ← Back to Email Stats
            </Link>
          </div>
        </div>
      )}

      <EmailStatsTemplatePreviewModal
        isOpen={templatePreviewOpen}
        onClose={() => setTemplatePreviewOpen(false)}
        templateId={log?.templateId}
        emailNumber={log?.emailNumber}
      />

      <Modal
        isModalOpen={jsonModalOpen}
        toggleModal={() => setJsonModalOpen(false)}
        title="Email log JSON"
        compact
        className="!bg-[#2D333B] border border-[#3D444D]"
        titleColor="text-gray-font"
        bodyClassName="!bg-[#2D333B] !pt-0"
      >
        <JsonViewer json={rawJson} />
        <div className="flex justify-end gap-2">
          <Button
            text="Copy"
            color="white"
            variant="outlined"
            size="fixed"
            onClick={() => {
              void handleCopyJson();
            }}
          />
          <Button
            text="Close"
            color="secondary"
            variant="filled"
            size="fixed"
            onClick={() => setJsonModalOpen(false)}
          />
        </div>
      </Modal>
    </main>
  );
}
